import React, { useEffect, useState } from 'react';
import { Button, Avatar, TwistButton, useToast } from '../../components/ui';
import { getPublicApiOrigin } from '../../config/env';

/**
 * T0 item A: collapsible "AI-screened candidates from CodeSense (n)" at the top of
 * the Candidates tab. Same data and actions as today's SyncedCandidatesSection:
 * select shortlisted → "Send invite (n)", and a collapsed "Not shortlisted" list.
 */
export default function AiScreenedPanel({ campaignId, onInvited }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [belowOpen, setBelowOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [invited, setInvited] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    fetch(`${getPublicApiOrigin()}/api/integration/campaigns/${campaignId}/synced-candidates`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData({ total: 0, shortlisted: 0, candidates: [] }); });
    return () => { cancelled = true; };
  }, [campaignId]);

  if (!data || !data.total) return null;
  const shortlisted = (data.candidates || []).filter((c) => c.is_shortlisted);
  const below = (data.candidates || []).filter((c) => !c.is_shortlisted);
  const allIds = shortlisted.map((c) => c.id);
  const allSel = allIds.length > 0 && allIds.every((x) => selected.includes(x));

  const send = async () => {
    if (!selected.length || inviting) return;
    setInviting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${getPublicApiOrigin()}/api/integration/campaigns/${campaignId}/invite-shortlisted`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ synced_candidate_ids: selected }),
      });
      const result = await res.json();
      if (res.ok) {
        setInvited(new Set([...invited, ...(result.sent || []).map((x) => x.id)]));
        setSelected([]);
        toast(`Invite sent to ${result.sent_count} candidate${result.sent_count !== 1 ? 's' : ''}`);
        onInvited?.();
      } else {
        toast(result.detail || 'Failed to send invites');
      }
    } catch {
      toast('Failed to send invites');
    } finally {
      setInviting(false);
    }
  };

  const row = (c, selectable) => (
    <div className="wz-ai-row" key={c.id}>
      <span>{selectable && <input type="checkbox" aria-label={`Select ${c.name || c.email}`} checked={selected.includes(c.id)} onChange={() => setSelected((p) => (p.includes(c.id) ? p.filter((x) => x !== c.id) : [...p, c.id]))} />}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <Avatar name={c.name || c.email} size={30} />
        <span className="wz-name wz-ellipsis" style={{ fontWeight: 500 }}>{c.name || '—'}</span>
      </span>
      <span className="wz-sub wz-ellipsis">{c.email || '—'}</span>
      <span className="wz-sub">{c.phone || '—'}</span>
      <span>
        {!selectable ? <span className="wz-tag muted">Below cutoff</span>
          : invited.has(c.id) ? <span className="wz-tag ai">Invited</span>
            : <span className="wz-tag ok">Shortlisted</span>}
      </span>
    </div>
  );

  return (
    <section className="wz-panel wz-ai-panel">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 16 }}>
        <button type="button" className="wz-ai-head" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h2 style={{ fontSize: 15 }}>AI-screened candidates from CodeSense ({data.total})</h2>
            <span className="wz-tag ok">{data.shortlisted} shortlisted</span>
          </span>
        </button>
        <TwistButton expanded={open} label={open ? 'Collapse AI-screened candidates' : 'Expand AI-screened candidates'} onClick={() => setOpen((v) => !v)} />
      </div>
      {open && (
        <>
          {shortlisted.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px', borderTop: '1px solid var(--wz-line-2)', background: 'var(--wz-soft)' }}>
              <label className="wz-check">
                <input type="checkbox" checked={allSel} onChange={() => setSelected(allSel ? [] : [...allIds])} />
                {selected.length ? `${selected.length} selected` : 'Select all shortlisted'}
              </label>
              {selected.length > 0 && (
                <Button small variant="primary" icon="mail" disabled={inviting} onClick={send}>
                  {inviting ? 'Sending…' : `Send invite (${selected.length})`}
                </Button>
              )}
            </div>
          )}
          {shortlisted.map((c) => row(c, true))}
          {below.length > 0 && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 20px', borderTop: '1px solid var(--wz-line-2)' }}>
                <TwistButton expanded={belowOpen} label={belowOpen ? 'Hide not shortlisted' : 'Show not shortlisted'} onClick={() => setBelowOpen((v) => !v)} />
                <span className="wz-sub" style={{ fontWeight: 500 }}>Not shortlisted</span>
                <span className="wz-count">{below.length}</span>
              </div>
              {belowOpen && below.map((c) => row(c, false))}
            </>
          )}
        </>
      )}
    </section>
  );
}
